import { useEffect, useState } from "react";
import { useOn } from "../../../../../../../hooks/useOn.ts";
import { useAppQuery } from "../../../../../../../http/useAppQuery.ts";
import { selectedAssetUuidStore } from "../../../../../../boardState/stores/selectedAssetUuidStore.ts";
import type { Asset, DetailedAsset } from "../../../../../types.ts";

export const useAssetNavigation = ({
  onAssetSelectionChange,
  assets,
}: {
  onAssetSelectionChange: () => void;
  assets: Asset[];
}) => {
  const [selectedAssetIndex, setSelectedAssetIndex] = useState(0);
  const { selectedAssetUuid } = selectedAssetUuidStore.useState();

  useEffect(() => {
    if (
      selectedAssetUuid &&
      assets.findIndex((asset) => asset.uuid === selectedAssetUuid) !==
        selectedAssetIndex
    ) {
      setSelectedAssetIndex(
        assets.findIndex((asset) => asset.uuid === selectedAssetUuid),
      );
    }
  }, [assets, selectedAssetIndex, selectedAssetUuid]);

  const selectPreviousAsset = () => {
    if (selectedAssetIndex > 0) {
      setSelectedAssetIndex((index) => index - 1);
      selectedAssetUuidStore.setSelectedAssetUuid(
        assets[selectedAssetIndex - 1].uuid,
      );
    }
    onAssetSelectionChange();
  };
  const selectNextAsset = () => {
    if (selectedAssetIndex < assets.length - 1) {
      setSelectedAssetIndex((index) => index + 1);
      selectedAssetUuidStore.setSelectedAssetUuid(
        assets[selectedAssetIndex + 1].uuid,
      );
    }
    onAssetSelectionChange();
  };

  // XXX: We prefetch next and previous images to reduce loading time when navigating through the image stripe.
  const previousAssetCachingQuery = useAppQuery<DetailedAsset>({
    queryKey: `assets/${assets[selectedAssetIndex - 1]?.uuid}`,
    enabled: !!assets[selectedAssetIndex - 1],
  });
  const nextAssetCachingQuery = useAppQuery<DetailedAsset>({
    queryKey: `assets/${assets[selectedAssetIndex + 1]?.uuid}`,
    enabled: !!assets[selectedAssetIndex + 1],
  });
  useEffect(() => {
    if (selectedAssetIndex > 0) {
      const previousImg = new Image();
      previousImg.src = previousAssetCachingQuery.data?.content.url ?? "";
    }
    if (selectedAssetIndex < assets.length - 1) {
      const nextImg = new Image();
      nextImg.src = nextAssetCachingQuery.data?.content.url ?? "";
    }
  }, [
    assets,
    selectedAssetIndex,
    previousAssetCachingQuery,
    nextAssetCachingQuery,
  ]);

  useOn("keydown", ({ key }) => {
    if (key === "ArrowLeft") selectPreviousAsset();
    if (key === "ArrowRight") selectNextAsset();
  });

  return {
    selectPreviousAsset,
    selectNextAsset,
    hasPreviousAsset: selectedAssetIndex > 0,
    hasNextAsset: selectedAssetIndex < assets.length - 1,
  };
};
